import axios from 'axios';
import config from '../config.json';
import { authService } from './authService';

export interface VpnPeer {
  id: string | number;
  name: string;
  tunnel_ip: string;
  enabled: boolean;
  last_handshake: string | null;
}

export interface VpnStatus {
  service_active: boolean;
  peer: VpnPeer | null;
  /**
   * Whether crm-api can hand back a CODE SIGNED Windows installer right now.
   * False whenever signing is unavailable for any reason — the button is
   * hidden and the existing config-file flow is used unchanged. An unsigned
   * installer is never offered: it would trip SmartScreen, which is the whole
   * problem the installer exists to remove.
   */
  installer_available?: boolean;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.prod ? config.urlBase.prod : config.urlBase.dev;
  }

  private getAuthHeaders() {
    const token = authService.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getContacts() {
    const response = await axios.get(`${this.baseUrl}/crm/contacts`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getServices() {
    const response = await axios.get(`${this.baseUrl}/crm/services`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getTimezones() {
    const response = await axios.get(`${this.baseUrl}/crm/misc/timezones`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // Generic method for any endpoint
  async get(endpoint: string) {
    const response = await axios.get(`${this.baseUrl}${endpoint}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async post(endpoint: string, data: any) {
    const response = await axios.post(`${this.baseUrl}${endpoint}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async put(endpoint: string, data: any) {
    const response = await axios.put(`${this.baseUrl}${endpoint}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async delete(endpoint: string) {
    const response = await axios.delete(`${this.baseUrl}${endpoint}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // Password reset - sends reset email to user
  async resetPassword(email: string) {
    const response = await axios.get(`${this.baseUrl}/crm/auth/reset-pw`, {
      params: { username: email.trim() }
    });
    return response.data;
  }

  // Change password with token
  async changePassword(token: string, newPassword: string) {
    const response = await axios.post(`${this.baseUrl}/crm/change-password`, {
      token,
      password: newPassword,
    });
    return response.data;
  }

  // User management methods
  async createUser(userData: any) {
    const response = await axios.post(`${this.baseUrl}/crm/contacts`, userData, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateUser(userId: number, userData: any) {
    const response = await axios.patch(`${this.baseUrl}/crm/contacts/${userId}`, userData, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateProfile(profileData: any) {
    const response = await axios.patch(`${this.baseUrl}/crm/myprofile`, profileData, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * Get the current user's profile information
   * @returns The current user profile data
   */
  async getMyProfile() {
    const response = await axios.get(`${this.baseUrl}/crm/myprofile`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteUser(userId: number) {
    const response = await axios.delete(`${this.baseUrl}/crm/contacts/${userId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // License management methods
  async getUserLicenses(userId: number) {
    const response = await axios.get(`${this.baseUrl}/crm/contacts/${userId}/licenses`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async addUserLicense(userId: number, serviceId: number) {
    const response = await axios.post(`${this.baseUrl}/crm/contacts/${userId}/licenses`, 
      { service_id: serviceId }, 
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async removeUserLicense(userId: number, licenseId: number) {
    const response = await axios.delete(`${this.baseUrl}/crm/contacts/${userId}/licenses/${licenseId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // Billing API methods
  async getBillingAccount(accountId: string) {
    const response = await axios.get(`${this.baseUrl}/billing/account/${accountId}`, {
      headers: this.getAuthHeaders(),
    });
    console.log('🔍 Billing account response:', response.data);
    return response.data;
  }

  async getBillingPayments(accountId: string) {
    const response = await axios.get(`${this.baseUrl}/billing/account/${accountId}/payments`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getBillingStatements(accountId: string) {
    const response = await axios.get(`${this.baseUrl}/billing/account/${accountId}/statements`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getOrders() {
    const response = await axios.get(`${this.baseUrl}/crm/orders`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getOrderDetails(orderId: string) {
    const response = await axios.get(`${this.baseUrl}/crm/orders/${orderId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // IT Asset Management endpoints
  async getAssets() {
    const response = await axios.get(`${this.baseUrl}/crm/assets`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }
  
  async getAsset(assetId: string) {
    const response = await axios.get(`${this.baseUrl}/crm/assets/${assetId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }
  
  async getVendors() {
    const response = await axios.get(`${this.baseUrl}/crm/vendors`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }
  
  async getCredentials() {
    const response = await axios.get(`${this.baseUrl}/crm/credentials`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createCredential(credentialData: any) {
    const response = await axios.post(`${this.baseUrl}/crm/credentials`, credentialData, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getAssetCredentials(assetId: string | number) {
    const response = await axios.get(`${this.baseUrl}/crm/assets/${assetId}/credentials`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // Audited reveal — the only endpoint that returns a decrypted password to the
  // portal. Break Glass credentials raise an internal Slack/Periscope alert on
  // every call, so callers should cache the result rather than re-request it.
  async revealCredential(credentialId: string) {
    const response = await axios.post(
      `${this.baseUrl}/crm/credentials/${credentialId}/reveal`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async makeCreditCardPayment(accountId: string, paymentData: any) {
    const response = await axios.post(`${this.baseUrl}/billing/account/${accountId}/payments/creditcard`, paymentData, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // ---- ACH (Stripe Financial Connections) ----

  async getStripePublishableKey(accountId: string) {
    const response = await axios.get(
      `${this.baseUrl}/billing/account/${accountId}/config/stripe-public-key`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getACHInfo(accountId: string) {
    const response = await axios.get(
      `${this.baseUrl}/billing/account/${accountId}/payments/ach/info`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createACHConnectSession(accountId: string, returnUrl?: string) {
    const response = await axios.post(
      `${this.baseUrl}/billing/account/${accountId}/payments/ach/connect-session`,
      { return_url: returnUrl },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async saveACHPaymentMethod(accountId: string, paymentMethodId: string, authorization: {
    text: string;
    type?: 'one_time' | 'recurring';
  }) {
    const response = await axios.post(
      `${this.baseUrl}/billing/account/${accountId}/payments/ach/save`,
      { payment_method_id: paymentMethodId, authorization },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async payWithACH(accountId: string, amount: string) {
    const response = await axios.post(
      `${this.baseUrl}/billing/account/${accountId}/payments/ach`,
      { amount },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async removeACHPaymentMethod(accountId: string) {
    const response = await axios.delete(
      `${this.baseUrl}/billing/account/${accountId}/payments/ach`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateBillingAccount(accountId: string, accountData: any) {
    const response = await axios.patch(`${this.baseUrl}/billing/account/${accountId}`, accountData, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async removeCreditCard(accountId: string) {
    try {
      // Try the old portal approach first - DELETE to the specific creditcard endpoint
      const response = await axios.delete(`${this.baseUrl}/billing/account/${accountId}/payments/creditcard`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      // If that fails, fall back to PATCH with empty credit_card_info
      console.log('DELETE endpoint not available, using PATCH fallback');
      const response = await axios.patch(`${this.baseUrl}/billing/account/${accountId}`, {
        automatic_payments: false,
        credit_card_info: {}
      }, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    }
  }

  // Support Ticket Methods
  async getTickets(params?: { search?: string; status?: string; sort?: string; limit?: number }) {
    const response = await axios.get(`${this.baseUrl}/crm/tickets`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getTicket(ticketId: string) {
    const response = await axios.get(`${this.baseUrl}/crm/tickets/${ticketId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createTicket(ticketData: { subject: string; description: string; priority?: string }) {
    const response = await axios.post(`${this.baseUrl}/crm/tickets`, ticketData, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async addTicketResponse(ticketId: string, description: string) {
    const response = await axios.post(`${this.baseUrl}/crm/tickets/${ticketId}`,
      { description },
      {
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  // Support Tools API methods
  async getSupportTools() {
    const response = await axios.get(`${this.baseUrl}/crm/support-tools`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async generateToolDownloadLink(toolId: string, validHours: number = 24) {
    const response = await axios.post(`${this.baseUrl}/crm/support-tools/generate-link`, 
      { tool_id: toolId, valid_hours: validHours }, 
      {
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  async trackToolDownload(toolId: string, downloadToken: string) {
    const response = await axios.post(`${this.baseUrl}/crm/support-tools/download`, 
      { tool_id: toolId, download_token: downloadToken }, 
      {
        headers: this.getAuthHeaders(),
      }
    );
    return response.data;
  }

  async getToolDownloadHistory() {
    const response = await axios.get(`${this.baseUrl}/crm/support-tools/download-history`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // Software Development as a Service (itp_sdaas)
  async getDevProjects() {
    const response = await axios.get(`${this.baseUrl}/crm/dev-projects`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getDevProjectRequests(projectId: number) {
    const response = await axios.get(`${this.baseUrl}/crm/dev-projects/${projectId}/requests`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createDevRequest(projectId: number, data: { title: string; body?: string; request_type?: string }) {
    const response = await axios.post(`${this.baseUrl}/crm/dev-projects/${projectId}/requests`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getDevRequest(requestId: number) {
    const response = await axios.get(`${this.baseUrl}/crm/dev-requests/${requestId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async postDevRequestMessage(requestId: number, body: string) {
    const response = await axios.post(`${this.baseUrl}/crm/dev-requests/${requestId}/messages`,
      { body },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Legacy Voice IP Whitelist
  async detectPublicIP() {
    const response = await axios.get(`${this.baseUrl}/crm/legacy-voice/detect-ip`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getLegacyVoiceWhitelistedIPs(serviceId: string) {
    const response = await axios.get(`${this.baseUrl}/crm/services/${serviceId}/legacy-voice/ips`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async addLegacyVoiceWhitelistedIP(serviceId: string, ipAddress: string, label: string) {
    const response = await axios.post(
      `${this.baseUrl}/crm/services/${serviceId}/legacy-voice/ips`,
      { ip_address: ipAddress, label },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async removeLegacyVoiceWhitelistedIP(serviceId: string, ipAddress: string) {
    const response = await axios.delete(
      `${this.baseUrl}/crm/services/${serviceId}/legacy-voice/ips/${ipAddress}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Managed VPN
  async getVpnStatus(): Promise<VpnStatus> {
    const response = await axios.get(`${this.baseUrl}/crm/vpn/me`, {
      headers: this.getAuthHeaders(),
    });
    // crm-api wraps every payload in a { result, errors, message } envelope.
    // Most methods here hand that envelope straight to the caller and let the
    // component dig out `.result`, but this one is typed as VpnStatus, so
    // unwrap here and keep the signature honest.
    return response.data?.result ?? response.data;
  }

  // Fetched once as text so the same response can render the QR code and
  // back the downloaded file — avoids requesting the config twice.
  async getVpnConfig(): Promise<{ filename: string; content: string }> {
    const response = await axios.get(`${this.baseUrl}/crm/vpn/me/config`, {
      headers: this.getAuthHeaders(),
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)"?/i);
    const filename = match?.[1] || 'ITP360-VPN.conf';
    return { filename, content: response.data as unknown as string };
  }

  /**
   * The one-click Windows installer. Fetched as a blob rather than linked
   * directly because the endpoint needs the bearer token, and because a
   * failure has to be catchable — the caller falls back to the config file.
   *
   * The exe embeds this person's own VPN private key, so it is per-user and
   * the filename says so.
   *
   * `autostart` decides whether the installer's connect-now tickbox arrives
   * ticked. WireGuard has no separate auto-start setting -- a tunnel that is on
   * when you shut down comes back at boot -- so this is the difference between
   * an always-on VPN and one the person turns on when they need it. Default off.
   */
  async getVpnInstaller(autostart = false): Promise<{ filename: string; blob: Blob }> {
    const response = await axios.get(`${this.baseUrl}/crm/vpn/me/installer`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
      params: autostart ? { autostart: 1 } : undefined,
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)"?/i);
    const filename = match?.[1] || 'ITP-VPN-Setup.exe';
    return { filename, blob: response.data as Blob };
  }
}

export const apiService = new ApiService();
